import React, { useState } from 'react';
import { apiService } from '../services/api';

const InviteMemberModal = ({ isOpen, onClose }) => {
  const [inviteLink, setInviteLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    hoursValid: 24,
    maxUses: 1
  });

  const generateInviteLink = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiService.generateInviteLink({
        hours_valid: settings.hoursValid,
        max_uses: settings.maxUses
      });
      console.log('Invite link generated:', response.data);
      setInviteLink(response.data.invite_link);
      setSuccess(true);
    } catch (error) {
      console.error('Generate invite link Fehler:', error);
      setError(error.response?.data?.error || 'Fehler beim Generieren des Einladungslinks');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      console.error('Copy to clipboard Fehler:', error);
      setError('Fehler beim Kopieren in die Zwischenablage');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-wood-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold text-wood-100 mb-4">Mitglied einladen</h2>
        
        <div className="space-y-4">
          {!inviteLink ? (
            <div>
              <p className="text-wood-300 mb-4">
                Erstellen Sie einen Einladungslink mit benutzerdefinierten Einstellungen. 
                Das neue Mitglied kann sich damit registrieren.
              </p>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-wood-200 mb-2">
                    Gültigkeitsdauer (Stunden)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={settings.hoursValid}
                    onChange={(e) => setSettings(prev => ({ ...prev, hoursValid: parseInt(e.target.value) || 24 }))}
                    className="w-full px-3 py-2 bg-wood-700 border border-wood-600 rounded-lg text-wood-100 focus:ring-2 focus:ring-beer-500 focus:border-transparent"
                    placeholder="24"
                  />
                  <p className="text-xs text-wood-400 mt-1">1-168 Stunden (1 Stunde bis 1 Woche)</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-wood-200 mb-2">
                    Maximale Verwendungen
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.maxUses}
                    onChange={(e) => setSettings(prev => ({ ...prev, maxUses: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 bg-wood-700 border border-wood-600 rounded-lg text-wood-100 focus:ring-2 focus:ring-beer-500 focus:border-transparent"
                    placeholder="1"
                  />
                  <p className="text-xs text-wood-400 mt-1">0 = unbegrenzt, 1-100 = begrenzt</p>
                </div>
              </div>
              
              <button
                onClick={generateInviteLink}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-beer-600 to-beer-700 hover:from-beer-700 hover:to-beer-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Erstelle Link...
                  </div>
                ) : (
                  'Einladungslink erstellen'
                )}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-wood-300 mb-4">
                Kopieren Sie den folgenden Link und senden Sie ihn an das neue Mitglied:
              </p>
              
              <div className="bg-wood-700 rounded-lg p-3 mb-4">
                <p className="text-wood-200 text-sm break-all">{inviteLink}</p>
              </div>
              
              <div className="bg-wood-600 rounded-lg p-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-wood-300">Gültigkeitsdauer:</span>
                  <span className="text-wood-100">{settings.hoursValid} Stunden</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-wood-300">Max. Verwendungen:</span>
                  <span className="text-wood-100">
                    {settings.maxUses === 0 ? 'Unbegrenzt' : `${settings.maxUses}x`}
                  </span>
                </div>
              </div>
              
              <button
                onClick={copyToClipboard}
                className="w-full bg-wood-600 hover:bg-wood-500 text-wood-100 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 mb-3"
              >
                In Zwischenablage kopieren
              </button>
              
              <button
                onClick={() => setInviteLink('')}
                className="w-full bg-wood-500 hover:bg-wood-400 text-wood-100 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Neuen Link erstellen
              </button>
            </div>
          )}
          
          {error && (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-3">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-900/50 border border-green-700 rounded-lg p-3">
              <p className="text-green-200 text-sm">
                {inviteLink ? 'Link in Zwischenablage kopiert!' : 'Einladungslink erstellt!'}
              </p>
            </div>
          )}
          
          <button
            onClick={onClose}
            className="w-full bg-wood-600 hover:bg-wood-500 text-wood-100 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
